<template>
  <div class="projet-part">
    <h2 id="diffusion">{{ $t("projetPage.mon-annee.diffusion.title") }}</h2>

    <fieldset>
      <legend>{{ $t("projetPage.mon-annee.diffusion.ecole") }}</legend>
      <InputWithValidation
        v-model="descriptionPassageFlambeauComputed"
        :validation-activated="validationActivated"
        :show-validation-errors="showValidationErrors"
        type="textarea"
        :label="$t('projetPage.mon-annee.diffusion.passage')"
        :input-notice="$t('projetPage.mon-annee.diffusion.passage-help')"
        maxlength="1400"
        :disabled="readonly || !isPorteurProjet"
        vid="diffusion_descriptionPassageFlambeau"
        @manual-change="modified"
      />
      <InputWithValidation
        v-model="communicationAutresClassesComputed"
        :validation-activated="validationActivated"
        :show-validation-errors="showValidationErrors"
        :rules="projetAnnuel.typeDossier === 'DOSSIER_RENOUVELLEMENT_LIGHT' ? '' : 'required'"
        type="textarea"
        :label="$t('projetPage.mon-annee.diffusion.communication')"
        maxlength="1400"
        :disabled="readonly || !isPorteurProjet"
        vid="diffusion_communicationAutresClasses"
        @manual-change="modified"
      />
    </fieldset>

    <fieldset>
      <legend>{{ $t("projetPage.mon-annee.diffusion.territoire") }}</legend>
      <InputWithValidation
        v-model="presentationProjetElusComputed"
        :rules="projetAnnuel.typeDossier === 'DOSSIER_RENOUVELLEMENT' ? 'required' : ''"
        :validation-activated="validationActivated"
        :show-validation-errors="showValidationErrors"
        type="textarea"
        :label="$t('projetPage.mon-annee.diffusion.presentation-elus')"
        maxlength="1400"
        :disabled="readonly || !isPorteurProjet"
        vid="diffusion_presentationProjetExterieur"
        @manual-change="modified"
      />
      <InputWithValidation
        v-model="rencontresActeursTerritoireComputed"
        :validation-activated="validationActivated"
        :show-validation-errors="showValidationErrors"
        :rules="projetAnnuel.typeDossier === 'DOSSIER_RENOUVELLEMENT' ? 'required' : ''"
        type="textarea"
        :label="$t('projetPage.mon-annee.diffusion.rencontre')"
        :input-notice="$t('projetPage.mon-annee.diffusion.rencontre-message')"
        maxlength="1400"
        :disabled="readonly || !isPorteurProjet"
        vid="diffusion_rencontresActeursTerritoire"
        @manual-change="modified"
      />

      <FileWithValidation
        :label="$t(`projetPage.mon-annee.etat-des-lieux.fichier-titre`)"
        :validation-activated="validationActivated"
        :show-validation-errors="showValidationErrors"
        :placeholder="$t(`projetPage.infoAdministratives.charte.televerser`)"
        :input-notice="$t('projetPage.mon-annee.diffusion.fichier')"
        :file-operation-url="adhesionDiffusionFileUrl"
        :disabled="readonly"
        :model-value="projetAnnuel.adhesionDiffusionFilePresent"
        default-file-name="ADHESION_DIFFUSION.pdf"
        @add-file="addAdhesionDiffusionFile"
        @delete-file="deleteAdhesionDiffusionFile"
      />
    </fieldset>

    <fieldset>
      <legend>{{ $t("projetPage.mon-annee.diffusion.ae") }}</legend>
      <InputWithValidation
        v-model="organisationEchangesInterAeComputed"
        :validation-activated="validationActivated"
        :show-validation-errors="showValidationErrors"
        type="textarea"
        :label="$t('projetPage.mon-annee.diffusion.echanges')"
        maxlength="1400"
        :disabled="readonly || !isPorteurProjet"
        vid="diffusion_organisationEchangesInterAe"
        @manual-change="modified"
      />
      <FileWithValidation
        :label="$t(`projetPage.mon-annee.etat-des-lieux.fichier-titre`)"
        :validation-activated="validationActivated"
        :show-validation-errors="showValidationErrors"
        :placeholder="$t(`projetPage.infoAdministratives.charte.televerser`)"
        :input-notice="$t('projetPage.mon-annee.diffusion.fichier-interae')"
        :file-operation-url="organisationEchangesInterAeFileUrl"
        :disabled="readonly"
        :model-value="projetAnnuel.organisationEchangesInterAePresent"
        default-file-name="ECHANGES_INTER_AE.pdf"
        @add-file="addInterAeDiffusionFile"
        @delete-file="deleteInterAeDiffusionFile"
      />
    </fieldset>

    <fieldset>
      <legend>{{ $t("projetPage.mon-annee.diffusion.enseignements") }}</legend>
      <b-message
        v-if="
          validationActivated &&
          (projetAnnuel.typeDossier === 'DOSSIER_LABELLISATION' ||
            projetAnnuel.typeDossier === 'DOSSIER_RENOUVELLEMENT') &&
          showRequiredMessage
        "
        type="is-danger"
      >
        {{ $t("projetPage.mon-annee.autres-infos.labellisation-renouvellement") }}
      </b-message>
      <InputWithValidation
        v-model="diffusionLiensEnseignementsComputed"
        :validation-activated="validationActivated"
        :show-validation-errors="showValidationErrors"
        type="textarea"
        :label="$t('projetPage.mon-annee.diffusion.liens-enseignements')"
        :rules="projetAnnuel.typeDossier === 'DOSSIER_RENOUVELLEMENT_LIGHT' ? '' : calculatedRules"
        maxlength="1400"
        :disabled="readonly || !isPorteurProjet"
        vid="diffusion_diffusionLiensEnseignements"
        @manual-change="modifiedLiens()"
      />
    </fieldset>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import { ProjetAnnuelDTO } from "@/model/bean/GeneratedDTOs"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import { FileService } from "@/services/rest/utils/FileService"
import FileWithValidation from "@/views/inputs/FileWithValidation.vue"
import { BMessage } from "buefy"

export default defineComponent({
  name: "ProjetDiffusionView",
  components: {
    InputWithValidation,
    FileWithValidation,
    BMessage,
  },
  props: {
    projetAnnuel: {
      type: Object as PropType<ProjetAnnuelDTO>,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    isPorteurProjet: {
      type: Boolean,
      default: false,
    },
    validationActivated: {
      type: Boolean,
      default: false,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["projet-annuel-updated", "dirtify-project", "trigger-validation"],
  data() {
    return {
      fileService: FileService.INSTANCE,
      calculatedRules: "required",
      showRequiredMessage: true,
      adhesionDiffusionFileUrl: "",
      organisationEchangesInterAeFileUrl: "",
    }
  },
  computed: {
    descriptionPassageFlambeauComputed: {
      get(): string {
        return this.projetAnnuel.descriptionPassageFlambeau || ""
      },
      set(value: string): void {
        const updatedProjet = { ...this.projetAnnuel }
        updatedProjet.descriptionPassageFlambeau = value
        this.$emit("projet-annuel-updated", updatedProjet)
      },
    },
    communicationAutresClassesComputed: {
      get(): string {
        return this.projetAnnuel.communicationAutresClasses || ""
      },
      set(value: string): void {
        const updatedProjet = { ...this.projetAnnuel }
        updatedProjet.communicationAutresClasses = value
        this.$emit("projet-annuel-updated", updatedProjet)
      },
    },
    presentationProjetElusComputed: {
      get(): string {
        return this.projetAnnuel.presentationProjetElus || ""
      },
      set(value: string): void {
        const updatedProjet = { ...this.projetAnnuel }
        updatedProjet.presentationProjetElus = value
        this.$emit("projet-annuel-updated", updatedProjet)
      },
    },
    rencontresActeursTerritoireComputed: {
      get(): string {
        return this.projetAnnuel.rencontresActeursTerritoire || ""
      },
      set(value: string): void {
        const updatedProjet = { ...this.projetAnnuel }
        updatedProjet.rencontresActeursTerritoire = value
        this.$emit("projet-annuel-updated", updatedProjet)
      },
    },
    organisationEchangesInterAeComputed: {
      get(): string {
        return this.projetAnnuel.organisationEchangesInterAe || ""
      },
      set(value: string): void {
        const updatedProjet = { ...this.projetAnnuel }
        updatedProjet.organisationEchangesInterAe = value
        this.$emit("projet-annuel-updated", updatedProjet)
      },
    },
    diffusionLiensEnseignementsComputed: {
      get(): string {
        return this.projetAnnuel.diffusionLiensEnseignements || ""
      },
      set(value: string): void {
        const updatedProjet = { ...this.projetAnnuel }
        updatedProjet.diffusionLiensEnseignements = value
        this.$emit("projet-annuel-updated", updatedProjet)
      },
    },
  },
  watch: {
    "projetAnnuel.problematiquesLiens": {
      handler(): void {
        this.updateDiffusionLiensRenseignementRules()
      },
    },
    "projetAnnuel.conseilsLiens": {
      handler(): void {
        this.updateDiffusionLiensRenseignementRules()
      },
    },
    "projetAnnuel.etatDesLieuxLiensEnseignements": {
      handler(): void {
        this.updateDiffusionLiensRenseignementRules()
      },
    },
    "projetAnnuel.diffusionLiensEnseignements": {
      handler(): void {
        this.updateDiffusionLiensRenseignementRules()
      },
    },
  },
  mounted(): void {
    this.updateDiffusionLiensRenseignementRules()
    if (this.projetAnnuel.id) {
      this.adhesionDiffusionFileUrl = this.fileService.buildUniqueFileURL(
        this.projetAnnuel.id,
        "ADHESION_DIFFUSION",
      )
      this.organisationEchangesInterAeFileUrl = this.fileService.buildUniqueFileURL(
        this.projetAnnuel.id,
        "ECHANGES_INTER_AE",
      )
    }
  },
  methods: {
    updateDiffusionLiensRenseignementRules(): void {
      if (
        !this.projetAnnuel.problematiquesLiens &&
        !this.projetAnnuel.conseilsLiens &&
        !this.projetAnnuel.etatDesLieuxLiensEnseignements
      ) {
        this.calculatedRules = "required"
      } else {
        if (this.projetAnnuel.diffusionLiensEnseignements) {
          this.calculatedRules = "required"
        } else {
          this.calculatedRules = ""
        }
      }

      this.showRequiredMessage = !(
        this.projetAnnuel.problematiquesLiens ||
        this.projetAnnuel.conseilsLiens ||
        this.projetAnnuel.diffusionLiensEnseignements ||
        this.projetAnnuel.etatDesLieuxLiensEnseignements
      )
    },

    modified(): void {
      this.$nextTick(() => {
        this.$emit("trigger-validation")
        this.$emit("dirtify-project")
      })
    },

    modifiedLiens(): void {
      this.updateDiffusionLiensRenseignementRules()
      this.modified()
    },

    addAdhesionDiffusionFile(): void {
      this.updateProjetAnnuelFile
    },

    deleteAdhesionDiffusionFile(): void {
      this.updateProjetAnnuelFile
    },

    addInterAeDiffusionFile(): void {
      this.updateProjetAnnuelFile("organisationEchangesInterAePresent", true)
    },

    deleteInterAeDiffusionFile(): void {
      this.updateProjetAnnuelFile("organisationEchangesInterAePresent", false)
    },

    updateProjetAnnuelFile<K extends keyof ProjetAnnuelDTO>(
      attributeName: K,
      isPresent: ProjetAnnuelDTO[K],
    ): void {
      const updatedProjet = { ...this.projetAnnuel }
      updatedProjet[attributeName] = isPresent
      this.$emit("projet-annuel-updated", updatedProjet)
      this.$emit("trigger-validation")
    },
  },
})
</script>

<style lang="scss"></style>
